import React, { useEffect, useState } from "react";
import { getCompetitionSlug } from "../../../utils/hooks/event.hooks";
import { useNavigate } from "react-router-dom";
import ChevronRightIconBorder from "../../../assets/icons/chevron-right-icon-border.svg";
import ChevronDownIconBorderBlue from "../../../assets/icons/chevron-down-icon-border-blue.svg";
import {
  useAppDispatch,
  useAppSelector,
  fetchCompetitions,
  fetchEventsByCompetition,
  EventDTO,
  setCompetitionThunk,
  setExchEvent,
  SportSlugById,
  CompetitionDTO,
  getExchangeEvents,
  SPToBFIdMap,
} from "@hypexone/uicore";

interface SportCompetitionsSectionProps {
  sportId: string;
  sportName: string;
}

const SportCompetitionsSection: React.FC<SportCompetitionsSectionProps> = ({
  sportId,
  sportName,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { competitions, events } = useAppSelector((state) => state.catalog);
  const { langData } = useAppSelector((state) => state.cms);
  const competitionsBySportId = competitions[sportId];
  const [openCompetitionIndices, setOpenCompetitionIndices] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (sportId && !competitionsBySportId) {
      dispatch(fetchCompetitions(sportId));
    }
  }, [sportId, competitionsBySportId, dispatch]);

  const toggleCompetition = (competitionId: string, eventList: EventDTO[]) => {
    // if (eventList.length === 0) {
    //   navigate(`/no-markets`);
    // }
    if (openCompetitionIndices.includes(competitionId)) {
      setOpenCompetitionIndices(
        openCompetitionIndices.filter((id) => id !== competitionId)
      );
    } else {
      setOpenCompetitionIndices([...openCompetitionIndices, competitionId]);
      const eventKey = SPToBFIdMap[sportId] ? SPToBFIdMap[sportId] : sportId;
      const compKey = competitionId?.split(":").join("_");
      const existing = getExchangeEvents(events, eventKey, compKey);
      if (!existing || existing.length === 0) {
        dispatch(
          fetchEventsByCompetition({
            sportId,
            competitionId,
            events: [],
          })
        );
      }
    }
  };

  const handleEventChange = (event: EventDTO) => {
    setCompetitionThunk({
      id: event.competitionId,
      name: event.competitionName,
      slug: getCompetitionSlug(event.competitionName),
    });
    setExchEvent({
      id: event.eventId,
      name: event.eventName,
      slug: event.eventSlug || "",
    });
    navigate(
      event?.providerName?.toLowerCase() === "sportradar" &&
        event?.catId === "SR VIRTUAL"
        ? `/exchange_sports/virtuals/${SportSlugById[event?.sportId]
        }/${getCompetitionSlug(event?.competitionName)}/${event?.eventSlug
        }/${btoa(
          `${event?.sportId}:${event?.competitionId}:${event?.eventId}`
        )}`
        : `/exchange_sports/${SportSlugById[event?.sportId]
        }/${getCompetitionSlug(event?.competitionName)}/${event?.eventSlug
        }/${btoa(
          `${event?.sportId}:${event?.competitionId}:${event?.eventId}`
        )}`
    );
  };

  const renderCompetitions = () => {
    if (!competitionsBySportId || competitionsBySportId.length === 0) {
      return (
        <div className="text-xs bg-gray17 border-b border-goldenYellow pl-4 pr-2 py-1 text-black">
          {langData?.["no_competitions_available"] ||
            "No competitions available"}
        </div>
      );
    }

    return competitionsBySportId.map((competition: CompetitionDTO, idx) => {
      const eventList: EventDTO[] = getExchangeEvents(
        events,
        SPToBFIdMap[sportId] ? SPToBFIdMap[sportId] : sportId,
        competition.id?.split(":").join("_")
      );

      return (
        <div key={competition.id + idx} className="text-xs">
          <div
            className="flex justify-between cursor-pointer bg-white border-b border-gray17 pl-4 pr-2 py-1.5 text-black hover:bg-gray1"
            onClick={() => toggleCompetition(competition.id, eventList)}
          >
            {/* Matches available in it; none shown until its events load. */}
            <span className="flex flex-1">
              {competition.name}
              {eventList?.length > 0 && ` (${eventList.length})`}
            </span>
            <span className="flex flex-0">
              {openCompetitionIndices.includes(competition.id) ? (
                <img
                  src={ChevronDownIconBorderBlue}
                  alt="down"
                  className="h-[15px] w-[15px]"
                />
              ) : (
                <img
                  src={ChevronRightIconBorder}
                  alt="right"
                  className="h-[15px] w-[15px]"
                />
              )}
            </span>
          </div>
          {openCompetitionIndices.includes(competition.id) &&
            eventList?.map((event: EventDTO) => (
              <div
                key={event.eventId}
                className="flex gap-[2px] pl-6 py-1.5 bg-gray17 border-b border-gray17 cursor-pointer text-black hover:bg-gray1"
                onClick={() => handleEventChange(event)}
              >
                {event.eventName}
              </div>
            ))}
          {eventList?.length === 0 &&
            openCompetitionIndices.includes(competition.id) && (
              <div className="text-xs bg-gray17 pl-6 py-1.5 border-b border-gray17 text-black">
                {langData?.["no_events_available"] || "No events available"}
              </div>
            )}
        </div>
      );
    });
  };

  return (
    <div className="text-[13px]">
      <div className="bg-black text-white capitalize px-3 py-1 border-b-2 border-goldenYellow">
        {sportName}
      </div>
      <div className="bg-gray17 text-black px-3 py-1.5 border-b border-goldenYellow">
        Top Competitions
      </div>
      <div className="">{renderCompetitions()}</div>
    </div>
  );
};

export default SportCompetitionsSection;
